import { useQuery } from '@tanstack/react-query'
import { useCurrentOrgId } from '@/stores/authStore'

import request, { type ComplexResponseType } from './request'
import { zodSubjectResponseValidator, type SubjectResponse } from './types/Subject'
import {
  zodExclusionWordResponseValidator,
  zodIndividualAliasResponseValidator,
  zodIndividualAssignmentResponseValidator,
  zodIndividualImageSearchRefinementResponseValidator,
  zodIndividualSocialPlatformGroupedResponseValidator,
  type AssignmentStatusType,
  type ExclusionWordResponse,
  type IndividualAliasResponse,
  type IndividualAssignmentResponse,
  type IndividualAssignmentTypesType,
  type IndividualImageSearchRefinementResponse,
  type IndividualSocialPlatformGroupedResponse
} from './types/Organization'
import { zodSponsorshipResponseValidator, type SponsorshipResponse } from './types/Sponsorship'

/* ---------------------------- Organization Individuals ---------------------------- */

const getOrganizationIndividuals = async ({
  page, limit, filterBy
}: {
  page: number,
  limit: number,
  filterBy?: string
}) => {
  const queryParams: string[] = []
  if (page > 1) queryParams.push('page=' + page)
  if (limit > 1) queryParams.push('limit=' + limit)
  if (filterBy) queryParams.push(`filter[name]=${filterBy}`)
  const res = await request<ComplexResponseType<SubjectResponse[]>>({
    endpoint: `/organization/individuals?${queryParams.join('&')}`,
    method: 'GET'
  })
  if (res.data?.items?.length) zodSubjectResponseValidator(res.data.items)
  return res
}

export const useGetOrganizationIndividuals = ({
  page, limit, filterBy
}: {
  page: number,
  limit: number,
  filterBy?: string
}, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'individuals', page, limit, filterBy],
    queryFn: () => getOrganizationIndividuals({
      page,
      limit,
      filterBy
    }),
    enabled
  })
}

export const updateOrganizationIndividual = async (
  organizationIndividualId: string,
  body: any
) => {
  return request<any>({
    endpoint: `/organization/individuals/${organizationIndividualId}`,
    method: 'PATCH',
    body,
    multipart: true,
  })
}

export const createOrganizationIndividual = async (body: any) => {
  return request<any>({
    endpoint: '/organization/individuals',
    method: 'POST',
    body,
    multipart: true,
  })
}

export const deleteOrganizationIndividual = async (organizationIndividualId: string) => {
  return request<any>({
    endpoint: `/organization/individuals/${organizationIndividualId}`,
    method: 'DELETE',
  })
}

export const uploadOrganizationIndividualsCSV = async (body: any) => {
  return request<any>({
    endpoint: '/organization/individuals/upload',
    method: 'POST',
    body,
    multipart: true,
  })
}

// /* ------- Individual Additional Details ------- */

// const getIndividualSearchRefinement = async (
//   subjectId: string
// ) => {
//   const res = await request<IndividualSearchRefinementResponse>({
//     endpoint: `/organization/individuals/${subjectId}/search-refinement`,
//     method: 'GET',
//   })
//   if (res.success) zodIndividualSearchRefinementResponseValidator(res.data)
//   return res
// }

// export const useGetIndividualSearchRefinement = (
//   subjectId: string | undefined,
//   enabled = true
// ) => {
//   const currOrgId = useCurrentOrgId()
//   return useQuery({
//     queryKey: [currOrgId, 'organization', 'individuals', subjectId, 'search-refinement'],
//     queryFn: () =>
//       getIndividualSearchRefinement(subjectId || ''),
//     enabled: !!subjectId && enabled,
//   })
// }

// export const updateIndividualSearchRefinement = async (
//   subjectId: string,
//   body: any
// ) => {
//   return request<any>({
//     endpoint: `/organization/individuals/${subjectId}/search-refinement`,
//     method: 'POST',
//     body,
//   })
// }

/* ------- Individual Search Assets ------- */

const getIndividualSearchAssetsRefinement = async (
  subjectId: string
) => {
  const res = await request<IndividualImageSearchRefinementResponse[]>({
    endpoint: `/organization/individuals/${subjectId}/search-assets-refinement`,
    method: 'GET',
  })
  if (res.success) zodIndividualImageSearchRefinementResponseValidator(res.data)
  return res
}

export const useGetIndividualSearchAssetsRefinement = (
  subjectId: string | undefined,
  enabled = true
) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'individuals', subjectId, 'search-assets-refinement'],
    queryFn: () =>
      getIndividualSearchAssetsRefinement(subjectId || ''),
    enabled: !!subjectId && enabled,
  })
}

export const updateIndividualSearchAssetsRefinement = async (
  subjectId: string,
  body: any
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/search-assets-refinement`,
    method: 'POST',
    body,
    multipart: true,
  })
}

export const deleteIndividualSearchAssetsRefinement = async (
  subjectId: string,
  body: any
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/search-assets-refinement`,
    method: 'DELETE',
    body,
  })
}

/* ------- Individual Assignments ------- */

const getIndividualAssignments = async ({
  subjectId,
  page,
  limit,
  filterBy,
  type,
  status,
}: {
  subjectId: string
  page: number
  limit: number
  filterBy?: string
  type?: IndividualAssignmentTypesType
  status?: AssignmentStatusType
}) => {
  const queryParams: string[] = []
  if (page > 1) queryParams.push('page=' + page)
  if (limit > 1) queryParams.push('limit=' + limit)
  if (filterBy) queryParams.push(`filter[name]=${filterBy}`)
  if (type) queryParams.push(`type=${type}`)
  if (status) queryParams.push(`status=${status}`)

  const res = await request<ComplexResponseType<IndividualAssignmentResponse[]>>({
    endpoint: `/organization/individuals/${subjectId}/assignments?${queryParams.join('&')}`,
    method: 'GET',
  })
  if (res.data?.items?.length) zodIndividualAssignmentResponseValidator(res.data.items)
  return res
}

export const useGetIndividualAssignments = ({
  subjectId,
  page,
  limit,
  filterBy,
  type,
  status,
}: {
  subjectId: string | undefined
  page: number
  limit: number
  filterBy?: string
  type?: IndividualAssignmentTypesType
  status?: AssignmentStatusType
},
  enabled = true
) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'individuals', subjectId, 'assignments', page, limit, filterBy, type, status],
    queryFn: () =>
      getIndividualAssignments({
        subjectId: subjectId || '',
        page,
        limit,
        filterBy,
        type,
        status,
      }),
    enabled: !!subjectId && enabled,
  })
}

export const createIndividualAssignment = async (
  subjectId: string,
  body: any,
  isMultipart = false
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/assignments`,
    method: 'POST',
    body,
    multipart: isMultipart,
  })
}

export const deleteIndividualAssignment = async (
  subjectId: string,
  body: any
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/assignments`,
    method: 'DELETE',
    body,
  })
}

export const uploadIndividualAssignmentsCSV = async (
  subjectId: string,
  body: any
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/assignments/upload`,
    method: 'POST',
    body,
    multipart: true,
  })
}

/* ------- Individual Sponsorships ------- */

const getIndividualSponsorships = async ({ subjectId, page, limit, filterBy }: {
  subjectId: string,
  page: number,
  limit: number,
  filterBy?: string
}) => {
  const queryParams: string[] = []
  if (page > 1) queryParams.push('page=' + page)
  if (limit > 1) queryParams.push('limit=' + limit)
  if (filterBy) queryParams.push(`filter[name]=${filterBy}`)

  const res = await request<ComplexResponseType<SponsorshipResponse[]>>({
    endpoint: `/organization/individuals/${subjectId}/sponsorships?${queryParams.join('&')}`,
    method: 'GET'
  })
  if (res.data?.items?.length) zodSponsorshipResponseValidator(res.data.items)
  return res
}

export const useGetIndividualSponsorships = ({
  subjectId,
  page,
  limit,
  filterBy
}: {
  subjectId: string | undefined,
  page: number,
  limit: number,
  filterBy?: string
}, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'individuals', subjectId, 'sponsorships', page, limit, filterBy],
    queryFn: () =>
      getIndividualSponsorships({
        subjectId: subjectId || '',
        page,
        limit,
        filterBy
      }),
    enabled: !!subjectId && enabled,
  })
}

export const updateIndividualSponsorship = async (
  subjectId: string,
  sponsorshipId: string,
  body: any
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/sponsorships/${sponsorshipId}`,
    method: 'PATCH',
    body,
  })
}

export const createIndividualSponsorship = async (
  subjectId: string,
  body: any
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/sponsorships`,
    method: 'POST',
    body,
  })
}

/* ------- Individual Social Platforms ------- */

const getIndividualSocialPlatforms = async (
  subjectId: string,
  trusted?: boolean
) => {
  const queryParams: string[] = []
  if (trusted !== undefined) {
    queryParams.push(`trusted=${trusted}`)
  }
  const queryString = queryParams.length ? `?${queryParams.join('&')}` : ''
  const res = await request<IndividualSocialPlatformGroupedResponse>({
    endpoint: `/organization/individuals/${subjectId}/social-platforms${queryString}`,
    method: 'GET',
  })
  if (res.success) zodIndividualSocialPlatformGroupedResponseValidator(res.data)
  return res
}

export const useGetIndividualSocialPlatforms = ({
  subjectId,
  trusted,
}: {
  subjectId: string | undefined,
  trusted?: boolean
}, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'individuals', subjectId, 'social-platforms', trusted],
    queryFn: () =>
      getIndividualSocialPlatforms(subjectId || '', trusted),
    enabled: !!subjectId && enabled,
  })
}

export const createIndividualSocialPlatform = async (
  subjectId: string,
  body: any,
  trusted?: boolean
) => {
  const queryParams: string[] = []
  if (trusted !== undefined) {
    queryParams.push(`trusted=${trusted}`)
  }
  const queryString = queryParams.length ? `?${queryParams.join('&')}` : ''
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/social-platforms${queryString}`,
    method: 'POST',
    body,
  })
}

export const deleteIndividualSocialPlatform = async (
  subjectId: string,
  subjectSocialPlatformId: string
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/social-platforms/${subjectSocialPlatformId}`,
    method: 'DELETE',
  })
}

/* ------- Individual Alias ------- */

const getIndividualAlias = async (
  subjectId: string
) => {
  const res = await request<{ alias: IndividualAliasResponse[] }>({
    endpoint: `/organization/individuals/${subjectId}/alias`,
    method: 'GET',
  })
  if (res.success) zodIndividualAliasResponseValidator(res.data.alias)
  return res
}

export const useGetIndividualAlias = (
  subjectId: string | undefined,
  enabled = true
) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'individuals', subjectId, 'alias'],
    queryFn: () =>
      getIndividualAlias(subjectId || ''),
    enabled: !!subjectId && enabled,
  })
}

export const createIndividualAlias = async (
  subjectId: string,
  body: any
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/alias`,
    method: 'POST',
    body,
  })
}

export const deleteIndividualAlias = async (
  subjectId: string,
  subjectAliasQueryId: string
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/alias/${subjectAliasQueryId}`,
    method: 'DELETE',
  })
}

/* ------- Individual Exclusion Words ------- */

const getIndividualExclusionWords = async (
  subjectId: string
) => {
  const res = await request<{ exclusionWords: ExclusionWordResponse[] }>({
    endpoint: `/organization/individuals/${subjectId}/exclusion-words`,
    method: 'GET',
  })
  if (res.success) zodExclusionWordResponseValidator(res.data.exclusionWords)
  return res
}

export const useGetIndividualExclusionWords = ({
  subjectId,
}: {
  subjectId: string | undefined,
}, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'individuals', subjectId, 'exclusion-words'],
    queryFn: () =>
      getIndividualExclusionWords(subjectId || ''),
    enabled: !!subjectId && enabled,
  })
}

export const createIndividualExclusionWord = async (
  subjectId: string,
  body: { word: string }
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/exclusion-words`,
    method: 'POST',
    body,
  })
}

export const deleteIndividualExclusionWord = async (
  subjectId: string,
  wordId: string
) => {
  return request<any>({
    endpoint: `/organization/individuals/${subjectId}/exclusion-words/${wordId}`,
    method: 'DELETE',
  })
}
